import { useCallback, useEffect, useRef, useState } from 'react';
import type { Environment } from '../types/environment';
import type { Task } from '../types/task';
import BranchSelector from './BranchSelector';
import ChevronIcon from './ChevronIcon';
import { fetchDefaultBranches } from '../utils/defaultBranches';

interface TaskEnvBranchProps {
  task: Task;
  workstreamID: string;
  onSaved?: (updated: { EnvironmentID?: string; RepoInfo?: Record<string, any>; Version: number }) => void;
}

export default function TaskEnvBranch({ task, workstreamID, onSaved }: TaskEnvBranchProps) {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [envLoading, setEnvLoading] = useState(true);
  const [defaultBranches, setDefaultBranches] = useState<Record<string, string>>({});
  const [branchesExpanded, setBranchesExpanded] = useState(false);
  const envChangeRef = useRef(0);

  const hasTurns = (task.LastTurnIndex ?? 0) > 0 || !!task.TurnID;
  const envLocked = hasTurns;

  useEffect(() => {
    let cancelled = false;
    setEnvLoading(true);
    fetch('/api/environments')
      .then(async (resp) => {
        if (cancelled || !resp.ok) return;
        const data: Environment[] = await resp.json();
        if (!cancelled) setEnvironments(data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setEnvLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const selectedEnv = environments.find((e) => e.EnvironmentID === task.EnvironmentID);

  // Fetch default branches when the selected environment changes.
  useEffect(() => {
    if (!selectedEnv) {
      setDefaultBranches({});
      return;
    }
    let cancelled = false;
    fetchDefaultBranches(selectedEnv.EnvironmentID, selectedEnv.Repos).then((map) => {
      if (!cancelled) setDefaultBranches(map);
    });
    return () => { cancelled = true; };
  }, [selectedEnv?.EnvironmentID, selectedEnv?.Repos]);

  const patchTask = useCallback(
    async (fields: Record<string, unknown>) => {
      try {
        const resp = await fetch(
          `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(task.TaskID)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Version: task.Version ?? 1, Fields: fields }),
          },
        );
        if (resp.ok) {
          const data = await resp.json();
          onSaved?.(data);
        }
      } catch {}
    },
    [workstreamID, task.TaskID, task.Version, onSaved],
  );

  const handleEnvChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newEnvID = e.target.value;
      const changeId = ++envChangeRef.current;
      const env = environments.find((en) => en.EnvironmentID === newEnvID);
      if (!env) {
        patchTask({ EnvironmentID: newEnvID, RepoInfo: {} });
        return;
      }

      // Fetch default branches, falling back to empty on failure.
      const defaults = await fetchDefaultBranches(newEnvID, env.Repos);

      // Bail out if a newer env change happened while we were fetching.
      if (changeId !== envChangeRef.current) return;

      setDefaultBranches(defaults);

      const repoInfo: Record<string, any> = {};
      env.Repos.forEach((repo) => {
        const key = `${repo.Org}/${repo.Repo}`;
        repoInfo[key] = { TargetBranch: defaults[key] || 'main' };
      });
      patchTask({ EnvironmentID: newEnvID, RepoInfo: repoInfo });
    },
    [environments, patchTask],
  );

  const handleBranchChange = useCallback(
    (repoKey: string, branch: string) => {
      const repoInfo: Record<string, any> = {};
      if (task.RepoInfo) {
        for (const [key, info] of Object.entries(task.RepoInfo)) {
          repoInfo[key] = {
            ...info,
            TargetBranch: key === repoKey ? branch : info.TargetBranch,
          };
        }
      }
      if (!repoInfo[repoKey]) {
        repoInfo[repoKey] = { TargetBranch: branch };
      }
      patchTask({ RepoInfo: repoInfo });
    },
    [task.RepoInfo, patchTask],
  );

  const repoEntries = selectedEnv
    ? selectedEnv.Repos.map((r) => {
        const key = `${r.Org}/${r.Repo}`;
        const branch = task.RepoInfo?.[key]?.TargetBranch || defaultBranches[key] || 'main';
        return { key, org: r.Org, repo: r.Repo, branch };
      })
    : [];

  return (
    <div style={{ marginTop: '12px' }}>
      {/* Environment */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
        <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '90px' }}>Environment</label>
        {envLoading ? (
          <span style={{ fontSize: '14px', color: '#656d76' }}>Loading...</span>
        ) : (
          <select
            value={task.EnvironmentID ?? ''}
            onChange={handleEnvChange}
            disabled={envLocked}
            style={{
              padding: '4px 8px',
              fontSize: '14px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              flex: 1,
              maxWidth: '600px',
              cursor: envLocked ? 'not-allowed' : 'pointer',
            }}
          >
            <option value="">— Select environment —</option>
            {environments.map((env) => (
              <option key={env.EnvironmentID} value={env.EnvironmentID}>
                {env.Name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Branches — one per repo */}
      {repoEntries.length === 1 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', fontWeight: 500, minWidth: '90px' }}>Branch</label>
          <div style={{ flex: 1, maxWidth: '600px' }}>
            <BranchSelector
              org={repoEntries[0].org}
              repo={repoEntries[0].repo}
              environmentID={task.EnvironmentID ?? ''}
              value={repoEntries[0].branch}
              onChange={(branch) => handleBranchChange(repoEntries[0].key, branch)}
            />
          </div>
        </div>
      ) : repoEntries.length > 1 ? (
        <div style={{ marginBottom: '8px' }}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setBranchesExpanded((e) => !e)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setBranchesExpanded((ex) => !ex);
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              userSelect: 'none',
              marginBottom: branchesExpanded ? '8px' : 0,
            }}
          >
            <span style={{
              display: 'inline-block',
              transform: branchesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
            }}>
              <ChevronIcon size={16} />
            </span>
            Branches
          </div>
          {branchesExpanded && repoEntries.map((entry) => (
            <div
              key={entry.key}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', paddingLeft: '20px' }}
            >
              <label
                style={{ fontSize: '14px', fontWeight: 500, minWidth: '90px' }}
                title={entry.key}
              >
                {entry.key}
              </label>
              <div style={{ flex: 1, maxWidth: '600px' }}>
                <BranchSelector
                  org={entry.org}
                  repo={entry.repo}
                  environmentID={task.EnvironmentID ?? ''}
                  value={entry.branch}
                  onChange={(branch) => handleBranchChange(entry.key, branch)}
                />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
